/**
 * Marketing Sync — R9 marketing mirror, read-only status page.
 * Shows mktg_sync_runs and mktg_mirror_leads tables.
 * If migration 026 has not been applied, renders a graceful empty state.
 */

import { Layout } from '@/components/layout/Layout';
import { useQueryClient } from '@tanstack/react-query';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  useMarketingSyncRuns,
  useMarketingMirrorStats,
  useMarketingMirrorLeads,
} from '@/hooks/use-marketing-sync';
import { useConsumeSyncStatus } from '@/hooks/use-consume-sync';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatUiNumber, formatUiRelativeTime } from '@/lib/ui-format';
import { toSafeUiError } from '@/lib/user-safe-error';
import { cn } from '@/lib/utils';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  DatabaseZap,
  HourglassIcon,
  Users,
  XCircle,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const TABLE_MISSING_CODES = new Set(['42P01', 'PGRST205']);

const isTableMissing = (error: Error | null): boolean => {
  if (!error) return false;
  const err = error as { code?: string; message?: string };
  if (err.code && TABLE_MISSING_CODES.has(err.code)) return true;
  if (err.message && err.message.toLowerCase().includes('does not exist')) return true;
  return false;
};

const statusBadgeClass = (status: string | null): string => {
  if (!status) return '';
  if (status === 'success' || status === 'ok') return 'border-green-500/40 bg-green-500/10 text-green-400';
  if (status === 'error' || status === 'failed') return 'border-red-500/40 bg-red-500/10 text-red-400';
  return 'border-amber-500/40 bg-amber-500/10 text-amber-400';
};

const modeBadgeClass = (mode: string | null): string => {
  if (!mode) return '';
  if (mode === 'full') return 'border-blue-500/40 bg-blue-500/10 text-blue-400';
  return 'border-muted bg-muted/30 text-muted-foreground';
};

const formatNullable = (value: string | null | undefined): string => value ?? '—';

interface SyncQueryIssue {
  area: string;
  error: Error;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function StatCardSkeleton(): React.ReactElement {
  return (
    <Card>
      <CardContent className="p-4 text-center">
        <Skeleton className="mx-auto h-5 w-5 rounded" />
        <Skeleton className="mx-auto mt-2 h-7 w-12" />
        <Skeleton className="mx-auto mt-1 h-3 w-20" />
      </CardContent>
    </Card>
  );
}

function TablesMissingState(): React.ReactElement {
  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <Card className="mx-auto max-w-md">
        <CardContent className="flex flex-col items-center gap-4 p-8 text-center">
          <DatabaseZap className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">Marketing Sync is not set up</h2>
            <p className="text-sm text-muted-foreground">
              This environment does not have the read-only marketing mirror yet. Complete the
              approved setup runbook to make sync status available.
            </p>
            <details className="mt-4 rounded-md border p-3 text-left text-xs">
              <summary
                className="flex min-h-11 cursor-pointer items-center font-medium"
                aria-label="Admin diagnostics for marketing setup"
              >
                Admin diagnostics
              </summary>
              <p className="mt-2 text-muted-foreground">
                Setup marker: <code>026_marketing_mirror</code>. No write action is available on
                this page.
              </p>
            </details>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface SyncErrorAlertProps {
  issues: SyncQueryIssue[];
  isRetrying: boolean;
  onRetry: () => void;
  partial?: boolean;
}

function SyncErrorAlert({
  issues,
  isRetrying,
  onRetry,
  partial = false,
}: SyncErrorAlertProps): React.ReactElement {
  const firstIssue = issues[0];
  const safeError = toSafeUiError(firstIssue?.error);

  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" aria-hidden="true" />
      <h2 className="mb-1 font-medium leading-none tracking-tight">
        {partial ? 'Some sync data is unavailable' : safeError.title}
      </h2>
      <AlertDescription className="space-y-3">
        <p>
          {partial
            ? 'Available sections remain read-only and may be incomplete.'
            : safeError.description}{' '}
          {safeError.recovery}
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="min-h-11 md:min-h-11"
          onClick={onRetry}
          disabled={isRetrying}
        >
          {isRetrying ? 'Retrying...' : 'Retry'}
        </Button>
        <details className="rounded-md border border-current/20 p-3 text-left text-xs">
          <summary
            className="flex min-h-11 cursor-pointer items-center font-medium"
            aria-label="Admin diagnostics for sync query errors"
          >
            Admin diagnostics
          </summary>
          <ul className="mt-2 space-y-1">
            {issues.map((issue) => {
              const issueSafeError = toSafeUiError(issue.error);
              return (
                <li key={issue.area}>
                  {issue.area}: {issueSafeError.kind}
                  {issueSafeError.requestId ? ` (${issueSafeError.requestId})` : ''}
                </li>
              );
            })}
          </ul>
        </details>
      </AlertDescription>
    </Alert>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Consume Mirrors section (028)
// ─────────────────────────────────────────────────────────────────────────────

function ConsumeMirrorsSection(): React.ReactElement {
  const consumeStatus = useConsumeSyncStatus();
  const queryClient = useQueryClient();
  const retry = (): void => {
    // Prefix match, deliberately. The tenancy sweep appended `(userId, organizationId)` AFTER this
    // root, so a bare prefix still reaches the identity-scoped entry — and reaching every identity
    // is right here: this is a manual Retry, and the only entry that can exist is the active one.
    void queryClient.invalidateQueries({ queryKey: ['consume-sync-runs'] });
  };

  if (consumeStatus.state === 'awaiting-apply') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            Configuration sync
            <Badge
              variant="outline"
              className="border-muted bg-muted/30 text-muted-foreground text-xs"
              data-testid="awaiting-apply-badge"
            >
              <HourglassIcon className="mr-1 h-3 w-3" aria-hidden="true" />
              Setup pending
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Read-only configuration status will appear after the approved production setup is
            complete.
          </p>
          <details className="mt-3 rounded-md border p-3 text-xs">
            <summary
              className="flex min-h-11 cursor-pointer items-center font-medium"
              aria-label="Admin diagnostics for configuration setup"
            >
              Admin diagnostics
            </summary>
            <p className="mt-2 text-muted-foreground">
              Setup marker: <code>028_consume_mirrors_sync</code>.
            </p>
          </details>
        </CardContent>
      </Card>
    );
  }

  if (consumeStatus.state === 'loading') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Configuration sync</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 p-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (consumeStatus.state === 'error') {
    const safeError = toSafeUiError(consumeStatus.error);

    return (
      <Card className="border-red-500/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base text-red-400">
            <XCircle className="h-4 w-4" aria-hidden="true" />
            Configuration sync is unavailable
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="font-medium text-red-400">{safeError.title}</p>
          <p className="text-sm text-red-400">
            {safeError.description} {safeError.recovery}
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="min-h-11 md:min-h-11"
            onClick={retry}
          >
            Retry
          </Button>
          <details className="rounded-md border border-red-500/20 p-3 text-xs">
            <summary
              className="flex min-h-11 cursor-pointer items-center font-medium"
              aria-label="Admin diagnostics for configuration sync"
            >
              Admin diagnostics
            </summary>
            <p className="mt-2">Configuration sync: {safeError.kind}</p>
          </details>
        </CardContent>
      </Card>
    );
  }

  // success
  const rows = Array.from(consumeStatus.latestByTable.values()).sort((a, b) =>
    a.table_name.localeCompare(b.table_name)
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Configuration sync</CardTitle>
      </CardHeader>
      <CardContent className="p-0 [&>div]:max-h-80">
        {rows.length === 0 ? (
          <p className="p-4 text-center text-sm text-muted-foreground">
            No consume sync runs yet.
          </p>
        ) : (
          <Table className="min-w-[760px]" aria-label="Configuration sync status">
            <TableHeader>
              <TableRow>
                <TableHead>Table</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead className="text-right">Upserted</TableHead>
                <TableHead className="text-right">Source</TableHead>
                <TableHead className="text-right">Mirror</TableHead>
                <TableHead className="text-right">Drift</TableHead>
                <TableHead>Last run</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((run) => {
                const drift =
                  run.source_count != null && run.mirror_count != null
                    ? run.mirror_count - run.source_count
                    : null;
                const hasDrift = drift != null && drift !== 0;
                return (
                  <TableRow key={run.table_name}>
                    <TableCell className="whitespace-nowrap text-xs font-mono">
                      {run.table_name}
                    </TableCell>
                    <TableCell>
                      {run.status ? (
                        <Badge
                          variant="outline"
                          className={cn('text-xs', statusBadgeClass(run.status))}
                        >
                          {run.status}
                        </Badge>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {run.mode ? (
                        <Badge
                          variant="outline"
                          className={cn('text-xs', modeBadgeClass(run.mode))}
                        >
                          {run.mode}
                        </Badge>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      {formatUiNumber(run.rows_upserted)}
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      {formatUiNumber(run.source_count)}
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      {formatUiNumber(run.mirror_count)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        'text-right text-xs',
                        hasDrift ? 'text-amber-400' : 'text-muted-foreground'
                      )}
                    >
                      {drift != null ? (drift > 0 ? `+${drift}` : String(drift)) : '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {run.started_at ? formatUiRelativeTime(run.started_at) : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

export default function MarketingSync(): React.ReactElement {
  useDocumentTitle('Marketing Sync — Tacit');

  const runsQuery = useMarketingSyncRuns();
  const statsQuery = useMarketingMirrorStats();
  const leadsQuery = useMarketingMirrorLeads();

  // Detect missing tables from any of the three queries
  const tablesMissing =
    isTableMissing(runsQuery.error) ||
    isTableMissing(statsQuery.error) ||
    isTableMissing(leadsQuery.error);

  const isLoading =
    runsQuery.isLoading || statsQuery.isLoading || leadsQuery.isLoading;
  const queryIssues: SyncQueryIssue[] = [
    ...(runsQuery.error && !isTableMissing(runsQuery.error)
      ? [{ area: 'Sync history', error: runsQuery.error }]
      : []),
    ...(statsQuery.error && !isTableMissing(statsQuery.error)
      ? [{ area: 'Mirror summary', error: statsQuery.error }]
      : []),
    ...(leadsQuery.error && !isTableMissing(leadsQuery.error)
      ? [{ area: 'Mirrored records', error: leadsQuery.error }]
      : []),
  ];
  const hasAvailableData =
    runsQuery.data !== undefined ||
    statsQuery.data !== undefined ||
    leadsQuery.data !== undefined;
  const hasCompleteFailure =
    !isLoading && queryIssues.length > 0 && !hasAvailableData;
  const hasPartialFailure =
    !isLoading && queryIssues.length > 0 && hasAvailableData;
  const isRetrying =
    runsQuery.isFetching || statsQuery.isFetching || leadsQuery.isFetching;

  const stats = statsQuery.data;
  const runs = runsQuery.data ?? [];
  const leads = leadsQuery.data ?? [];

  const lastRun = runs[0] ?? null;

  const qproCount = stats?.qproCount ?? 0;
  const felixIsolationOk = qproCount === 0;
  const isHealthyEmpty =
    !isLoading &&
    queryIssues.length === 0 &&
    runs.length === 0 &&
    leads.length === 0 &&
    (stats?.total ?? 0) === 0;
  const retryQueries = (): void => {
    void Promise.all([
      runsQuery.refetch(),
      statsQuery.refetch(),
      leadsQuery.refetch(),
    ]);
  };

  return (
    <Layout>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Header */}
        <div className="border-b bg-card p-4">
          <h1 className="text-2xl font-semibold tracking-tight">Marketing Sync</h1>
          <p className="text-sm text-muted-foreground">
            Read-only status for marketing mirror health and data freshness.
          </p>
        </div>

        {tablesMissing ? (
          <TablesMissingState />
        ) : hasCompleteFailure ? (
          <div className="flex flex-1 items-center justify-center p-4">
            <div className="w-full max-w-xl">
              <SyncErrorAlert
                issues={queryIssues}
                isRetrying={Boolean(isRetrying)}
                onRetry={retryQueries}
              />
            </div>
          </div>
        ) : (
          <div className="min-w-0 flex-1 overflow-x-clip p-4">
            <div className="mx-auto max-w-5xl space-y-6">
              {hasPartialFailure && (
                <SyncErrorAlert
                  issues={queryIssues}
                  isRetrying={Boolean(isRetrying)}
                  onRetry={retryQueries}
                  partial
                />
              )}

              {isHealthyEmpty && (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                  <h2 className="mb-1 font-medium leading-none tracking-tight">
                    Sync is healthy and currently empty
                  </h2>
                  <AlertDescription>
                    No mirror activity or records have been reported yet.
                  </AlertDescription>
                </Alert>
              )}

              {/* Stat Cards */}
              <div
                className="grid grid-cols-2 gap-3 sm:grid-cols-4"
                aria-busy={isLoading}
              >
                {isLoading && (
                  <span className="sr-only" role="status">
                    Loading marketing sync status.
                  </span>
                )}
                {/* Mirror Leads total */}
                {isLoading ? (
                  <>
                    <StatCardSkeleton />
                    <StatCardSkeleton />
                    <StatCardSkeleton />
                    <StatCardSkeleton />
                  </>
                ) : (
                  <>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <Users
                          className="mx-auto h-5 w-5 text-blue-400"
                          aria-hidden="true"
                        />
                        <p className="mt-1 text-2xl font-bold">
                          {formatUiNumber(stats?.total)}
                        </p>
                        <p className="text-xs text-muted-foreground">Mirror leads</p>
                      </CardContent>
                    </Card>

                    {/* Last sync */}
                    <Card>
                      <CardContent className="p-4 text-center">
                        <Clock
                          className="mx-auto h-5 w-5 text-amber-400"
                          aria-hidden="true"
                        />
                        <p className="mt-1 text-sm font-bold">
                          {stats?.lastSyncedAt
                            ? formatUiRelativeTime(stats.lastSyncedAt)
                            : '—'}
                        </p>
                        {lastRun?.mode && (
                          <Badge
                            variant="outline"
                            className={cn('mt-1 text-xs', modeBadgeClass(lastRun.mode))}
                          >
                            {lastRun.mode}
                          </Badge>
                        )}
                        <p className="mt-1 text-xs text-muted-foreground">Last sync</p>
                      </CardContent>
                    </Card>

                    {/* Last run status */}
                    <Card>
                      <CardContent className="p-4 text-center">
                        {lastRun?.status === 'success' || lastRun?.status === 'ok' ? (
                          <CheckCircle2
                            className="mx-auto h-5 w-5 text-green-400"
                            aria-hidden="true"
                          />
                        ) : lastRun?.status === 'error' || lastRun?.status === 'failed' ? (
                          <XCircle
                            className="mx-auto h-5 w-5 text-red-400"
                            aria-hidden="true"
                          />
                        ) : (
                          <Clock
                            className="mx-auto h-5 w-5 text-muted-foreground"
                            aria-hidden="true"
                          />
                        )}
                        <p className="mt-1 text-sm font-bold">
                          {lastRun?.status ?? '—'}
                        </p>
                        <p className="text-xs text-muted-foreground">Last run</p>
                      </CardContent>
                    </Card>

                    {/* Felix isolation */}
                    <Card
                      className={cn(
                        'border',
                        felixIsolationOk
                          ? 'border-green-500/30 bg-green-500/5'
                          : 'border-red-500/30 bg-red-500/5'
                      )}
                    >
                      <CardContent className="p-4 text-center">
                        {felixIsolationOk ? (
                          <CheckCircle2
                            className="mx-auto h-5 w-5 text-green-400"
                            aria-hidden="true"
                          />
                        ) : (
                          <AlertTriangle
                            className="mx-auto h-5 w-5 text-red-400"
                            aria-hidden="true"
                          />
                        )}
                        <p className="mt-1 text-sm font-bold">Source isolation</p>
                        {!felixIsolationOk && (
                          <p className="mt-1 text-xs text-red-400">
                            Restricted-source records detected. Review the mirror before relying
                            on this data.
                          </p>
                        )}
                        <Badge
                          variant="outline"
                          className={cn(
                            'mt-1 text-xs',
                            felixIsolationOk
                              ? 'border-green-500/40 bg-green-500/10 text-green-400'
                              : 'border-red-500/40 bg-red-500/10 text-red-400'
                          )}
                        >
                          {felixIsolationOk ? 'Clear' : 'Review required'}
                        </Badge>
                        <details className="mt-2 rounded-md border p-2 text-left text-xs">
                          <summary
                            className="flex min-h-11 cursor-pointer items-center font-medium"
                            aria-label="Admin diagnostics for source isolation"
                          >
                            Admin diagnostics
                          </summary>
                          <p className="mt-1 text-muted-foreground">
                            qpro-outsourcing records: {formatUiNumber(qproCount)}
                          </p>
                        </details>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>

              {/* Sync Runs Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Last 10 Sync Runs</CardTitle>
                </CardHeader>
                <CardContent className="p-0 [&>div]:max-h-80">
                  {runsQuery.isLoading ? (
                    <div className="space-y-2 p-4">
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-10 w-full" />
                      ))}
                    </div>
                  ) : runs.length === 0 ? (
                    <p className="p-4 text-center text-sm text-muted-foreground">
                      No sync runs yet.
                    </p>
                  ) : (
                    <Table className="min-w-[720px]" aria-label="Recent sync runs">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Started</TableHead>
                          <TableHead>Mode</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Upserted</TableHead>
                          <TableHead className="text-right">Source</TableHead>
                          <TableHead className="text-right">Mirror</TableHead>
                          <TableHead>Error</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {runs.map((run) => (
                          <TableRow key={run.id}>
                            <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                              {run.started_at
                                ? formatUiRelativeTime(run.started_at)
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {run.mode ? (
                                <Badge
                                  variant="outline"
                                  className={cn('text-xs', modeBadgeClass(run.mode))}
                                >
                                  {run.mode}
                                </Badge>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell>
                              {run.status ? (
                                <Badge
                                  variant="outline"
                                  className={cn('text-xs', statusBadgeClass(run.status))}
                                >
                                  {run.status}
                                </Badge>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell className="text-right text-xs">
                              {formatUiNumber(run.rows_upserted)}
                            </TableCell>
                            <TableCell className="text-right text-xs">
                              {formatUiNumber(run.source_count)}
                            </TableCell>
                            <TableCell className="text-right text-xs">
                              {formatUiNumber(run.mirror_count)}
                            </TableCell>
                            <TableCell className="max-w-xs truncate text-xs text-red-400">
                              {run.error ? 'Recorded' : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Consume Mirrors (028) */}
              <ConsumeMirrorsSection />

              {/* Mirror Leads Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Latest 20 Mirror Leads</CardTitle>
                </CardHeader>
                <CardContent className="p-0 [&>div]:max-h-80">
                  {leadsQuery.isLoading ? (
                    <div className="space-y-2 p-4">
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-10 w-full" />
                      ))}
                    </div>
                  ) : leads.length === 0 ? (
                    <p className="p-4 text-center text-sm text-muted-foreground">
                      No mirrored leads yet.
                    </p>
                  ) : (
                    <Table className="min-w-[720px]" aria-label="Latest mirrored leads">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Company</TableHead>
                          <TableHead>Title</TableHead>
                          <TableHead>Campaign</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Heat</TableHead>
                          <TableHead>Updated</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {leads.map((lead) => (
                          <TableRow key={lead.id}>
                            <TableCell className="whitespace-nowrap text-sm font-medium">
                              <span data-content-origin="external">
                                {[lead.first_name, lead.last_name].filter(Boolean).join(' ') || '—'}
                              </span>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              <span data-content-origin="external">
                                {formatNullable(lead.company_name)}
                              </span>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              <span data-content-origin="external">
                                {formatNullable(lead.job_title)}
                              </span>
                            </TableCell>
                            <TableCell>
                              {lead.campaign_key ? (
                                <Badge variant="outline" className="text-xs">
                                  {lead.campaign_key}
                                </Badge>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell className="text-xs">
                              {formatNullable(lead.status)}
                            </TableCell>
                            <TableCell className="text-xs">
                              {formatNullable(lead.heat_level)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                              {lead.updated_at
                                ? formatUiRelativeTime(lead.updated_at)
                                : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
